import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import { useLocation } from "wouter";
import { CANONICAL_ORIGIN } from "@/ssr/routes";

interface SEOProps {
  title?: string;
  description?: string;
  image?: string;
  url?: string;
  keywords?: string;
  noIndex?: boolean;
  preloadImage?: string;
}

export default function SEO({
  title,
  description,
  image = "/images/logo.jpg",
  url = CANONICAL_ORIGIN,
  keywords,
  noIndex = false,
  preloadImage,
}: SEOProps) {
  const { i18n } = useTranslation();
  const [location] = useLocation();
  const currentLang = i18n.language === "tr" ? "tr" : "en";
  const baseUrl = url.replace(/\/$/, "");
  const path = location || "/";
  const englishUrl = `${baseUrl}${path}?lang=en`;
  const turkishUrl = `${baseUrl}${path}?lang=tr`;
  const canonicalUrl = currentLang === "tr" ? turkishUrl : englishUrl;
  const fullImage = image.startsWith("http") ? image : `${baseUrl}${image}`;

  const fullTitle = title
    ? `${title} | Istanbul Escape`
    : "Istanbul Escape | Istanbul & Türkiye Travel";
  const fallbackDescription =
    currentLang === "tr"
      ? "İstanbul turlarını, Türkiye seyahat deneyimlerini, kişiye özel rota planlamasını ve VIP transfer desteğini İstanbul Escape ile keşfedin."
      : "Explore Istanbul tours, Türkiye travel experiences, custom itinerary planning, and VIP transfer support with Istanbul Escape.";
  const pageDescription = description || fallbackDescription;
  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "TravelAgency",
    name: "Istanbul Escape",
    url: CANONICAL_ORIGIN,
    logo: `${CANONICAL_ORIGIN}/images/logo.jpg`,
    image: fullImage,
    email: "info@istanbulescape.com",
    telephone: "+905352257703",
    address: {
      "@type": "PostalAddress",
      addressLocality: "Istanbul",
      addressCountry: "TR",
    },
    sameAs: ["https://www.instagram.com/escapeistanbul/"],
    inLanguage: currentLang,
  };

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="description" content={pageDescription} />
      <meta
        name="robots"
        content={
          noIndex ? "noindex,follow" : "index,follow,max-image-preview:large"
        }
      />
      {keywords && <meta name="keywords" content={keywords} />}
      <html lang={currentLang} dir={i18n.dir()} />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content="website" />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={pageDescription} />
      <meta property="og:image" content={fullImage} />
      <meta property="og:site_name" content="Istanbul Escape" />
      <meta
        property="og:locale"
        content={currentLang === "tr" ? "tr_TR" : "en_US"}
      />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={canonicalUrl} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={pageDescription} />
      <meta name="twitter:image" content={fullImage} />

      {/* Hreflang Tags for SEO */}
      <link rel="canonical" href={canonicalUrl} />
      <link rel="alternate" hrefLang="en" href={englishUrl} />
      <link rel="alternate" hrefLang="tr" href={turkishUrl} />
      <link rel="alternate" hrefLang="x-default" href={englishUrl} />
      {preloadImage && (
        <link
          rel="preload"
          as="image"
          href={preloadImage}
          fetchPriority="high"
        />
      )}
      <script type="application/ld+json">
        {JSON.stringify(organizationSchema)}
      </script>
    </Helmet>
  );
}
